import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { addReport, setActiveReport } from "../../state/actions";
import ReactMarkdown from "react-markdown";
import LinkCard from "../LinkCard";
import ReportCard from "../ReportCard";
import "./Message.scss";
import Accordion from "../Accordion";
import NewsArticlesAccordian from "../NewsArticlesAccordian";
import ChartBlock from "../ChartBlock";
import TableBlock from "../TableBlock";
import SourcesPill from "../SourcesPill";
import ErrorBanner from "../ErrorBanner";
import { useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import { appendHandoffParam, openAssistantLink } from "../../utils/assistantHandoff";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";

const userInfo = JSON.parse(
  localStorage.getItem(AI_ASSISTANT_CONSTANTS.STORAGE_KEYS.USER_INFO),
);
const userInitial =
  userInfo?.firstName?.charAt(0).toUpperCase() ||
  AI_ASSISTANT_CONSTANTS.MESSAGE.DEFAULT_USER_INITIAL;

const MarkdownLink = ({ node: _node, href, onClick, ...props }) => {
  const { conversationId } = useAssistantAbly();
  const resolvedHref = appendHandoffParam(href, conversationId);

  const handleClick = (event) => {
    onClick?.(event);
    openAssistantLink(href, conversationId, { event });
  };

  return (
    <a
      target={AI_ASSISTANT_CONSTANTS.MESSAGE.MARKDOWN_LINK_TARGET}
      rel={AI_ASSISTANT_CONSTANTS.MESSAGE.MARKDOWN_LINK_REL}
      {...props}
      href={resolvedHref}
      onClick={handleClick}
    />
  );
};

const Message = ({ msg, previousType, scrollToBottom }) => {
  const senderType = msg.type || AI_ASSISTANT_CONSTANTS.MESSAGE.DEFAULT_SENDER;
  const payload = { ...(msg.payload || {}) };
  const fallbackFields = AI_ASSISTANT_CONSTANTS.MESSAGE.FALLBACK_FIELDS;

  fallbackFields.forEach((field) => {
    if (msg[field] !== undefined && payload[field] === undefined) {
      payload[field] = msg[field];
    }
  });

  const {
    text = "",
    assumption,
    link,
    title,
    description,
    sources = [],
    chartConfig = {},
    tableConfig = { columns: [], rows: [] },
    report_id,
    report_data,
    report_status,
    report_status_message,
    report_title,
    error,
    warning,
    auto_dismiss_at,
    auto_dismiss_ms,
  } = payload;
  const message_type =
    msg.message_type || AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.TEXT;
  const textContent = text || "";
  const assumptionText = assumption || textContent;
  const errorText =
    error ||
    textContent ||
    AI_ASSISTANT_CONSTANTS.ERRORS.UI_UNEXPECTED;
  const warningText =
    warning ||
    textContent ||
    AI_ASSISTANT_CONSTANTS.ERRORS.REQUEST_TAKING_LONGER;
  const [warningCountdownSeconds, setWarningCountdownSeconds] = useState(null);
  const messageRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const messageElement = messageRef.current;
    if (messageElement) {
      setTimeout(() => {
        messageElement.classList.add("show");
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (message_type !== AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.WARNING) {
      setWarningCountdownSeconds(null);
      return undefined;
    }

    const dismissAt = Number(auto_dismiss_at);
    if (!Number.isFinite(dismissAt) || dismissAt <= 0) {
      setWarningCountdownSeconds(null);
      return undefined;
    }

    const updateCountdown = () => {
      const remainingMs = Math.max(0, dismissAt - Date.now());
      setWarningCountdownSeconds(Math.ceil(remainingMs / 1000));
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 250);
    return () => clearInterval(intervalId);
  }, [message_type, auto_dismiss_at, auto_dismiss_ms]);

  const containsMarkdown = (text) => /[*_~`[\]()]|https?:\/\//i.test(text);
  const renderTextMessage = () => (
    <div className={senderType}>
      <div className="markupdiv">
        {senderType === AI_ASSISTANT_CONSTANTS.MESSAGE.DEFAULT_SENDER && (
          <div className="username-letter">
            <span>{userInitial}</span>
          </div>
        )}
        <div className="message-content">
          {containsMarkdown(textContent) ? (
            <ReactMarkdown
              components={{
                a: MarkdownLink,
              }}
            >
              {textContent}
            </ReactMarkdown>
          ) : (
            <span className="message-plain-text">{textContent}</span>
          )}
          {sources && sources.length > 0 && message_type !== "sources" && (
            <SourcesPill sources={sources} />
          )}
        </div>
      </div>
    </div>
  );

  const renderLinkMessage = () => (
    <div className={senderType}>
      <LinkCard
        title={title || AI_ASSISTANT_CONSTANTS.MESSAGE.DEFAULT_LINK_TITLE}
        description={description || textContent}
        link={link}
      />
    </div>
  );

  const renderAssumptionMessage = () => (
    <Accordion title={AI_ASSISTANT_CONSTANTS.MESSAGE.DEFAULT_ASSUMPTIONS_TITLE}>
      <p>{assumptionText}</p>
    </Accordion>
  );

  const renderNewsSummaryMessage = () => (
    <SourcesPill sources={sources} />
  );

  const renderChartConfigMessage = () => (
    <ChartBlock highchartsConfig={chartConfig || {}} />
  );

  const renderTableMessage = () => (
    <TableBlock
      columns={tableConfig?.columns || []}
      rows={tableConfig?.rows || []}
    />
  );

  const renderReportMessage = () => (
    <div className={senderType}>
      <ReportCard
        report_title={report_title}
        report_status={report_status}
        report_status_message={report_status_message}
        onClick={() => {
          if (
            report_status === AI_ASSISTANT_CONSTANTS.REPORT.STATUS.SUCCESS
          ) {
            const report = {
              id: report_id,
              title: report_title,
              data: report_data,
            };
            dispatch(addReport(report));
            dispatch(setActiveReport(report_id));
          }
        }}
      />
    </div>
  );

  const renderErrorMessage = () => (
    <div className={senderType}>
      <ErrorBanner message={errorText} variant="error" />
    </div>
  );

  const renderWarningMessage = () => (
    <div className={senderType}>
      <ErrorBanner
        message={
          warningCountdownSeconds === null
            ? warningText
            : `${warningText} (disappears in ${warningCountdownSeconds}s)`
        }
        variant="warning"
      />
    </div>
  );

  const renderByType = () => {
    switch (message_type) {
      case AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.LINK:
        return renderLinkMessage();
      case AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.ASSUMPTION:
        return renderAssumptionMessage();
      case AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.SOURCES:
        return renderNewsSummaryMessage();
      case AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.CHART:
        return renderChartConfigMessage();
      case AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.TABLE:
        return renderTableMessage();
      case AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.REPORT:
        return renderReportMessage();
      case AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.ERROR:
        return renderErrorMessage();
      case AI_ASSISTANT_CONSTANTS.MESSAGE.TYPES.WARNING:
        return renderWarningMessage();
      default:
        return renderTextMessage();
    }
  };

  return (
    <li
      ref={messageRef}
      className={`msg-text ${senderType === "You" ? "align-right" : "align-left"}`}
    >
      {renderByType()}
    </li>
  );
};

export default Message;

MarkdownLink.propTypes = {
  node: PropTypes.any,
  href: PropTypes.string,
  onClick: PropTypes.func,
};

Message.propTypes = {
  msg: PropTypes.shape({
    type: PropTypes.string,
    message_type: PropTypes.string,
    message_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    payload: PropTypes.object,
    text: PropTypes.string,
    assumption: PropTypes.string,
    link: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    sources: PropTypes.array,
    chartConfig: PropTypes.object,
    tableConfig: PropTypes.object,
    report_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    report_data: PropTypes.object,
    report_status: PropTypes.string,
    report_status_message: PropTypes.string,
    report_title: PropTypes.string,
    error: PropTypes.string,
    warning: PropTypes.string,
  }).isRequired,
  previousType: PropTypes.string,
  scrollToBottom: PropTypes.func.isRequired,
};
